<script lang="ts">
	import { Auth } from "@utils/Auth.svelte.js";
	import { clarityEvent, clarityTag, clarityUpgrade } from "@utils/clarity.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import box from "@core/assets/icons/box.svg?raw";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import giftIcon from "@core/assets/icons/gift.svg?raw";
	import leaf from "@core/assets/icons/leaf.svg?raw";
	import mailIcon from "@core/assets/icons/mail.svg?raw";
	import shield from "@core/assets/icons/shield.svg?raw";
	import sparklesIcon from "@core/assets/icons/sparkles.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { Locale } from "@core/schema/Locale.js";
	import { SubscriptionDeliveryCount } from "@core/schema/SubscriptionDeliveryCount.js";
	import { SubscriptionFrequency } from "@core/schema/SubscriptionFrequency.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { SubscriptionSelection } from "@core/schema/SubscriptionSelection.js";
	import { SubscriptionSize } from "@core/schema/SubscriptionSize.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { subscriptionSizeNameMap } from "@core/schema/names/subscriptionSizeNameMap.js";
	import { subscriptionVariantNameMap } from "@core/schema/names/subscriptionVariantNameMap.js";
	import { getCartPath } from "@core/schema/paths/getCartPath.js";
	import { subscriptionGiftNoteMaxLength } from "@core/schema/subscriptionGiftNoteMaxLength.js";
	import { calculateMembershipShippingDate } from "@core/schema/utils/calculateMembershipShippingDate.js";
	import { calculateSubscriptionSavings } from "@core/schema/utils/calculateSubscriptionSavings.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { subscriptionDeliveryCountToNumberMap } from "@core/schema/utils/subscriptionDeliveryCountToNumberMap.js";
	import { subscriptionFrequencyToWeeksMap } from "@core/schema/utils/subscriptionFrequencyToWeeksMap.js";
	import { subscriptionVariantIconMap } from "@core/schema/utils/subscriptionVariantIconMap.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { formatPercentage } from "@core/utils/formatPercentage.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import EcoFriendlyTip from "./EcoFriendlyTip.svelte";
	import FathersChoiceB2cOnly from "./FathersChoiceB2cOnly.svelte";
	import FathersChoiceCancellable from "./FathersChoiceCancellable.svelte";
	import ProductCardMedia from "./ProductCardMedia.svelte";
	import Stepper from "./Stepper.svelte";
	import SubcategoryButton from "./SubcategoryButton.svelte";
	import SubscriptionActivationSelectionCard from "./SubscriptionActivationSelectionCard.svelte";
	import SubscriptionConfiguratorGroup from "./SubscriptionConfiguratorGroup.svelte";
	import translations from "./SubscriptionConfiguratorModal.translations.json" with { type: "json" };
	import SubscriptionDiscountChip from "./SubscriptionDiscountChip.svelte";
	import SubscriptionSelectionCard from "./SubscriptionSelectionCard.svelte";
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import SideModal from "./common/SideModal.svelte";
	import DatePicker from "./form/DatePicker.svelte";
	import LabelledInput from "./form/LabelledInput.svelte";
	import LabelledTextarea from "./form/LabelledTextarea.svelte";
	import { handleInvalidInputs } from "./form/handleInvalidInputs.js";

	const { tenant, mothersBlendProduct, cafeinaProduct, subcategories, exchangeRatesSetting, countrySettings } =
		getGlobalTemplateProps();
	const { MOTHERS_BLEND_CONSTANT_PRICE_ENABLED } = getGlobalSettings();

	let { show = $bindable(false) }: { show?: boolean } = $props();

	export function open(kind?: SubscriptionKind): void {
		if (kind !== undefined) {
			Cart.saveSubscriptionFields({ kind });
		}
		show = true;
	}

	const cart = $derived(Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery));
	const isGift = $derived(cart.subscription.kind === SubscriptionKind.Gift);
	const maxStep = $derived(isGift ? 3 : 2);

	let completed = false;
	let startsAtField = $state<HTMLDivElement>();
	let startsAtReported = $state(false);
	let giftFields = $state<HTMLFormElement>();

	let previousShow = show;
	$effect(() => {
		if (show && !previousShow) {
			clarityUpgrade("subscriptionConfigurator");
			clarityTag("subscriptionKind", cart.subscription.kind);
			clarityEvent("subscriptionConfiguratorOpened");
			completed = false;
			startsAtReported = false;
		} else if (!show && previousShow && !completed) {
			clarityEvent("subscriptionConfiguratorAbandoned");
		}
		previousShow = show;
	});

	let previousStep: number | undefined;
	$effect(() => {
		const currentStep = cart.subscriptionModalStep;
		if (!show) {
			previousStep = undefined;
			return;
		}
		if (previousStep !== undefined && currentStep !== previousStep) {
			startsAtReported = false;
			if (currentStep > previousStep) {
				// Steps are 0-indexed; report human numbering, so reaching the second screen is "Step2".
				clarityEvent(`subscriptionConfiguratorStep${currentStep + 1}`);
			} else {
				clarityEvent("subscriptionConfiguratorBack");
			}
		}
		previousStep = currentStep;
	});

	const translate = getTranslate(translations);

	const isDesktop = createReactiveBreakpoint(">2xl");

	const weeksInflect = {
		[Locale.cs]: makeInflect("týden", "týdny", "týdnů"),
		[Locale.en]: makeInflect("week", "weeks", "weeks"),
	};

	const today = $derived.by(() => {
		void cart.subscriptionModalStep;

		const now = new Date();
		now.setHours(0, 0, 0, 0);
		return now;
	});

	const selectedWeeks = $derived(subscriptionFrequencyToWeeksMap[cart.subscription.frequency]);
	const shippingDateRange = $derived(calculateMembershipShippingDate(today, cart.subscription.frequency));

	const savings = $derived.by(() => {
		if (Cart.destinationCountry === undefined || Cart.vatType === undefined || selectedCurrency.current === undefined) {
			return undefined;
		}

		const { withTax, percentage } = calculateSubscriptionSavings({
			subcategories,
			destinationCountry: Cart.destinationCountry,
			subscription: cart.subscription,
			mothersBlendProduct,
			cafeinaProduct,
			currency: selectedCurrency.current,
			vatType: Cart.vatType,
			exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
			countrySettings,
			mothersBlendConstantPriceEnabled: MOTHERS_BLEND_CONSTANT_PRICE_ENABLED,
			deliveryCount: cart.giftData.deliveryCount,
		});

		if (withTax <= 0 || percentage <= 0) {
			return undefined;
		}

		return {
			withTax: formatPrice(withTax, selectedCurrency.current, tenant.locale, false),
			percentage: formatPercentage(percentage, tenant.locale, false),
		};
	});

	const nextDispatchDate = $derived.by(() => {
		if (!cart.subscription.startsAt) {
			return undefined;
		}
		const date = new Date(cart.subscription.startsAt);
		date.setDate(date.getDate() + selectedWeeks * 7);
		return date;
	});

	const startsAtMissing = $derived(Number.isNaN(Date.parse(cart.subscription.startsAt)));
	const lastStep = $derived(cart.subscriptionModalStep >= maxStep - 1);

	const pickedDate = $derived.by(() => {
		if (startsAtMissing) {
			return undefined;
		}
		return new Date(cart.subscription.startsAt);
	});
	const isEmailSentToday = $derived(
		pickedDate?.getDate() === today.getDate() &&
			pickedDate.getMonth() === today.getMonth() &&
			pickedDate.getFullYear() === today.getFullYear(),
	);
	const threeMonthsFromToday = $derived.by(() => {
		const date = new Date(today);
		date.setMonth(date.getMonth() + 3);
		return date;
	});

	const monthsInflect = {
		[Locale.cs]: makeInflect("měsíc", "měsíce", "měsíců"),
		[Locale.en]: makeInflect("month", "months", "months"),
	};

	const packageInflect = {
		[Locale.cs]: makeInflect("zásilka", "zásilky", "zásilek"),
		[Locale.en]: makeInflect("package", "packages", "packages"),
	};

	const spoilByEmail = $derived(cart.giftData.spoilByEmail);
	const giftNote = $derived(cart.giftData.giftNote ?? "");
	const activateGiftSubscriptionImmediately = $derived(cart.activateGiftImmediately);
	const startsAtApplies = $derived(!isGift || activateGiftSubscriptionImmediately || spoilByEmail);
	const startsAtInvalid = $derived(startsAtReported && startsAtApplies && startsAtMissing);

	function submit(): void {
		if (startsAtApplies && startsAtMissing) {
			startsAtReported = true;
			startsAtField?.scrollIntoView({ behavior: "smooth", block: "center" });
			return;
		}
		Cart.addSubscription(() => {
			completed = true;
			clarityEvent("subscriptionConfiguratorCompleted");
			location.assign(getCartPath(tenant));
		});
	}
</script>

<SideModal bind:show fullContentWidth heading={translate(`heading${cart.subscription.kind}`)}>
	<Stepper max={maxStep} bind:current={cart.subscriptionModalStep} />
	<div
		class="grow shrink px-4 md:px-8 overflow-y-auto overscroll-contain flex flex-col gap-6 sm:gap-4 pb-10 pt-4 md:pt-12 sm:pt-8"
	>
		{#if cart.subscriptionModalStep === 0}
			<SubscriptionConfiguratorGroup order={1} label={translate("variantChoose")}>
				<div class="grid grid-cols-2 gap-x-3">
					{#each Object.values(SubscriptionVariant) as variant (variant)}
						<SubcategoryButton
							role="radio"
							class="!w-auto"
							active={cart.subscription.variant === variant}
							aria-checked={cart.subscription.variant === variant}
							onclick={(): void => {
								Cart.saveSubscriptionFields({
									variant,
									...(variant === SubscriptionVariant.Filter && { selection: SubscriptionSelection.FathersChoice }),
								});
							}}
							icon={subscriptionVariantIconMap[variant]}
							label={subscriptionVariantNameMap[variant][tenant.locale]}
						/>
					{/each}
				</div>
			</SubscriptionConfiguratorGroup>
			<SubscriptionConfiguratorGroup order={2} label={translate("selectionChoose")}>
				<div class="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 md:gap-3.5">
					{#each Object.values(SubscriptionSelection) as selection (selection)}
						{#if selection === SubscriptionSelection.FathersChoice}
							<SubscriptionSelectionCard
								title={translate("fathersChoice")}
								subtitle={translate("fathersChoiceTitle")}
								description={translate(`fathersChoiceDescription${cart.subscription.variant}`)}
								horizontal={cart.subscription.variant === SubscriptionVariant.Filter}
								active={cart.subscription.selection === selection}
								class={[cart.subscription.variant === SubscriptionVariant.Filter && "col-span-full"]}
								onclick={(): void => {
									Cart.saveSubscriptionFields({ selection });
								}}
							>
								<div class="inset-0 absolute bg-fcrbeige flex items-center justify-center user-select-none">
									<div
										class="rounded-full border-2 border-dashed border-fcrdarkgray/25 flex items-center justify-center h-1/2 aspect-square"
									>
										<span class="text-5xl/none">?</span>
									</div>
								</div>
								<div
									class="absolute items-center top-2.5 right-2.5 mr-px hidden xl:flex gap-1.5 text-white bg-fcrdarkgray rounded-full px-3 py-2"
								>
									<Icon icon={sparklesIcon} class="size-3" />
									<span class="uppercase text-xs/none [font-feature-settings:'ss04'_1] tracking-[.1em]">
										<Formatted text={translate("surprise")} />
									</span>
								</div>
							</SubscriptionSelectionCard>
						{:else if cart.subscription.variant === SubscriptionVariant.Espresso}
							{@const product = {
								[SubscriptionSelection.MothersBlend]: mothersBlendProduct,
								[SubscriptionSelection.Cafeina]: cafeinaProduct,
							}[selection]}
							{@const mediaSrc = getProductPrimaryMedia(product, [])}
							<SubscriptionSelectionCard
								title={product.name[tenant.locale]}
								subtitle={translate(`title${selection}`)}
								description={translate(`description${selection}`)}
								active={cart.subscription.selection === selection}
								horizontal={false}
								onclick={(): void => {
									Cart.saveSubscriptionFields({ selection });
								}}
							>
								{#if mediaSrc}
									<ProductCardMedia {mediaSrc} alt="" />
								{/if}
							</SubscriptionSelectionCard>
						{/if}
					{/each}
				</div>
			</SubscriptionConfiguratorGroup>
		{/if}
		{#if cart.subscriptionModalStep === 1}
			<SubscriptionConfiguratorGroup order={3} label={translate("sizeChoose")}>
				<div class="grid grid-cols-3 md:grid-cols-2 xl:grid-cols-3 gap-1 md:gap-3.5">
					{#each Object.values(SubscriptionSize) as size (size)}
						<Button
							aria-checked={cart.subscription.size === size}
							variant={ButtonVariant.Subcategory}
							size={ButtonSize.Custom}
							class="aria-checked:border-fcrdarkgray border-2 h-12 sm:h-14 lg:h-16 xl:h-18 px-3 sm:px-4 md:px-5 lg:px-6"
							onclick={(): void => {
								Cart.saveSubscriptionFields({ size });
							}}
						>
							<div class="flex flex-col md:flex-row md:gap-1.5 w-full justify-center">
								<span class="flex items-center justify-center gap-1.5 font-sans">
									{#if size === SubscriptionSize.G250x2 && cart.subscription.selection === SubscriptionSelection.FathersChoice}
										<span><Formatted text={translate(`size${size}${cart.subscription.selection}`)} /></span>
									{:else}
										<span><Formatted text={translate(`size${size}`)} /></span>
									{/if}
								</span>
								<span class="text-2xs/none md:text-base font-normal text-fcrgray">
									<Formatted text="({subscriptionSizeNameMap[size][tenant.locale]})" />
								</span>
							</div>
						</Button>
					{/each}
				</div>
			</SubscriptionConfiguratorGroup>
			<SubscriptionConfiguratorGroup order={4} label={translate("frequencyChoose")}>
				<div class="grid grid-cols-4 sm:grid-cols-5 gap-2">
					{#each Object.values(SubscriptionFrequency) as frequency (frequency)}
						{@const weeks = subscriptionFrequencyToWeeksMap[frequency]}
						<Button
							aria-checked={cart.subscription.frequency === frequency}
							variant={ButtonVariant.Subcategory}
							size={ButtonSize.Custom}
							class={[
								"aria-checked:outline aria-checked:outline-2 aria-checked:outline-fcrdarkgray",
								"h-18 px-3 sm:px-4 md:px-5 lg:px-6 [&:is(button)]:rounded-lg",
							]}
							onclick={(): void => {
								Cart.saveSubscriptionFields({ frequency });
							}}
						>
							<span class="flex items-center flex-col gap-0.5 w-full font-sans">
								<span class="text-[.625rem]/none tracking-[0.025rem] text-fcrgray font-normal">
									<Formatted text={translate("each")} />
								</span>
								<span class="text-1.5xl/6">
									<Formatted text={weeks.toString()} />
								</span>
								<span class="text-[.625rem]/none tracking-[0.025rem] text-fcrgray font-normal">
									<Formatted text={weeksInflect[tenant.locale](weeks)} />
								</span>
							</span>
						</Button>
					{/each}
				</div>
			</SubscriptionConfiguratorGroup>
			{#if isGift}
				<SubscriptionConfiguratorGroup order={5} label={translate("deliveryCountChoose")}>
					<div class="grid grid-cols-3 gap-2">
						{#each Object.values(SubscriptionDeliveryCount) as deliveryCount (deliveryCount)}
							{@const count = subscriptionDeliveryCountToNumberMap[deliveryCount]}
							{@const months = Math.ceil((count * selectedWeeks) / 4.345)}
							<Button
								aria-checked={Cart.state?.giftData.deliveryCount === deliveryCount}
								variant={ButtonVariant.Subcategory}
								size={ButtonSize.Custom}
								class={[
									"aria-checked:outline aria-checked:outline-2 aria-checked:outline-fcrdarkgray",
									"h-22 p-3 sm:p-4 md:p-5 lg:p-6 [&:is(button)]:rounded-lg",
								]}
								onclick={(): void => {
									Cart.saveGiftDataFields({ deliveryCount });
								}}
							>
								<span class="flex items-center flex-col gap-1 w-full font-sans">
									<span class="text-xl/[1.1]">
										<Formatted text="{count} {packageInflect[tenant.locale](count)}" />
									</span>
									<span class="text-xs/none tracking-[0.025rem] text-fcrgray font-normal">
										<Formatted
											text={translate("deliveryCountMonths", {
												months: `${months} ${monthsInflect[tenant.locale](months)}`,
											})}
										/>
									</span>
								</span>
							</Button>
						{/each}
					</div>
					<div class="text-xs/tighter text-fcrgray">
						<Formatted text={translate("giftSubscriptionLifetimeNote")} />
					</div>
				</SubscriptionConfiguratorGroup>
			{:else}
				<EcoFriendlyTip />
				<SubscriptionConfiguratorGroup order={5} label={translate("startsAt")}>
					<DatePicker
						up
						bind:element={startsAtField}
						bind:value={cart.subscription.startsAt}
						min={shippingDateRange.nearest}
						max={shippingDateRange.farthest}
						excludedDays={shippingDateRange.excludedDays}
						validationMessage={startsAtInvalid ? translate("startsAtRequired") : undefined}
					/>
					{#if nextDispatchDate}
						<div class="text-xs/tighter text-fcrgray">
							<Formatted
								text={translate("nextDispatchDate", { date: toDateMonthYearFormat(nextDispatchDate, tenant.locale) })}
							/>
						</div>
					{/if}
				</SubscriptionConfiguratorGroup>
				<FathersChoiceCancellable />
			{/if}
		{/if}
		{#if isGift && cart.subscriptionModalStep === 2}
			<SubscriptionConfiguratorGroup order={6} label={translate("activationChoose")}>
				<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{#each [true, false] as activateGiftImmediately (activateGiftImmediately)}
						<SubscriptionActivationSelectionCard
							checked={activateGiftImmediately === activateGiftSubscriptionImmediately}
							label={translate(`activateImmediately:${activateGiftImmediately}`)}
							description={translate(`activateImmediatelyLabel:${activateGiftImmediately}`)}
							icon={calendarIcon}
							onclick={(): void => {
								if (activateGiftImmediately !== activateGiftSubscriptionImmediately) {
									Cart.saveSubscriptionFields({ startsAt: "" });
								}
								Cart.saveActivateGiftImmediately(activateGiftImmediately);
							}}
						/>
					{/each}
				</div>
			</SubscriptionConfiguratorGroup>
			<SubscriptionConfiguratorGroup order={7} label={translate("notificationsChoose")}>
				<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{#each [false, true] as spoilByEmailOption (spoilByEmailOption)}
						<SubscriptionActivationSelectionCard
							checked={spoilByEmailOption === spoilByEmail}
							label={translate(`spoilByEmail:${spoilByEmailOption}`)}
							description={translate(`spoilByEmailLabel:${spoilByEmailOption}`)}
							icon={spoilByEmailOption ? mailIcon : giftIcon}
							onclick={(): void => {
								if (!spoilByEmailOption && !activateGiftSubscriptionImmediately) {
									Cart.saveSubscriptionFields({ startsAt: "" });
								}
								Cart.saveGiftDataFields({ spoilByEmail: spoilByEmailOption });
							}}
						/>
					{/each}
				</div>
				{#if !startsAtApplies}
					<div class="text-xs/tighter text-fcrgray">
						<Formatted text={translate("emailAtToDonorTodayNote")} />
					</div>
				{/if}
			</SubscriptionConfiguratorGroup>
			{#if startsAtApplies}
				<SubscriptionConfiguratorGroup
					order={8}
					label={translate(activateGiftSubscriptionImmediately ? "startsAt" : "emailAt")}
				>
					<DatePicker
						bind:element={startsAtField}
						bind:value={cart.subscription.startsAt}
						min={activateGiftSubscriptionImmediately ? shippingDateRange.nearest : today}
						max={activateGiftSubscriptionImmediately ? shippingDateRange.farthest : threeMonthsFromToday}
						excludedDays={activateGiftSubscriptionImmediately ? shippingDateRange.excludedDays : []}
						validationMessage={startsAtInvalid ? translate("startsAtRequired") : undefined}
					/>
					{#if pickedDate}
						<div class="text-xs/tighter text-fcrgray">
							{#if activateGiftSubscriptionImmediately}
								<Formatted
									text={translate("startsAtNote", { date: toDateMonthYearFormat(pickedDate, tenant.locale) })}
								/>
							{:else if isEmailSentToday}
								<Formatted text={translate("emailAtToRecipientTodayNote")} />
							{:else}
								<Formatted
									text={translate("emailAtToRecipientNote", {
										date: toDateMonthYearFormat(pickedDate, tenant.locale),
									})}
								/>
							{/if}
						</div>
					{/if}
				</SubscriptionConfiguratorGroup>
			{/if}
			<SubscriptionConfiguratorGroup order={startsAtApplies ? 9 : 8} label={translate("recipient")}>
				<form
					class="grid grid-cols-1 gap-2.5 gap-y-1.5 sm:grid-cols-2"
					bind:this={giftFields}
					use:handleInvalidInputs
					onsubmit={(event: SubmitEvent): void => {
						event.preventDefault();
						submit();
					}}
				>
					<LabelledInput
						required
						autocomplete="off"
						value={Cart.state?.giftData.name ?? ""}
						oninput={(event: Event & { currentTarget: HTMLInputElement }): void => {
							Cart.saveGiftDataFields({ name: event.currentTarget.value });
						}}
					>
						<Formatted text={translate("nameLabel")} />
					</LabelledInput>
					<LabelledInput
						type="email"
						required
						autocomplete="off"
						value={Cart.state?.giftData.email ?? ""}
						oninput={(event: Event & { currentTarget: HTMLInputElement }): void => {
							Cart.saveGiftDataFields({ email: event.currentTarget.value });
						}}
					>
						<Formatted text={translate("emailLabel")} />
					</LabelledInput>
					<LabelledInput
						required
						autocomplete="name"
						value={Cart.state?.giftData.donorName ?? ""}
						oninput={(event: Event & { currentTarget: HTMLInputElement }): void => {
							Cart.saveGiftDataFields({ donorName: event.currentTarget.value });
						}}
					>
						<Formatted text={translate("donorNameLabel")} />
					</LabelledInput>
					<LabelledInput
						required
						autocomplete="tel"
						value={Cart.state?.giftData.phoneNumber ?? ""}
						oninput={(event: Event & { currentTarget: HTMLInputElement }): void => {
							Cart.saveGiftDataFields({ phoneNumber: event.currentTarget.value });
						}}
					>
						<Formatted text={translate("phoneNumberLabel")} />
					</LabelledInput>
					<LabelledTextarea
						maxlength={subscriptionGiftNoteMaxLength}
						value={giftNote}
						oninput={(event: Event & { currentTarget: HTMLTextAreaElement }): void => {
							Cart.saveGiftDataFields({ giftNote: event.currentTarget.value });
						}}
					>
						<div class="flex justify-between items-end text-fcrgray text-sm/6 font-medium">
							<div><Formatted text={translate("giftNoteLabel")} /></div>
							<small><Formatted text="{giftNote.length} / {subscriptionGiftNoteMaxLength}" /></small>
						</div>
					</LabelledTextarea>
				</form>
			</SubscriptionConfiguratorGroup>
		{/if}
		{#if Auth.customerType !== CustomerType.B2c}
			<FathersChoiceB2cOnly />
		{/if}
	</div>
	<div
		class={[
			"flex flex-col grow-0 gap-2 xl:gap-3.5 shrink-0",
			"bg-fcrbeige px-4 md:px-12 pb-[max(env(safe-area-inset-bottom,0),0.5rem)] xl:pb-4 2xl:pb-6",
			"sticky md:static w-full z-10 bottom-0",
		]}
	>
		<div class="flex flex-col xl:gap-4 gap-1 text-fcrdarkgray font-medium pt-4 md:pt-4 2xl:pt-6">
			<div class="flex flex-col gap-1">
				<div class="text-xs/none md:text-sm/none pb-px">
					<Formatted text={translate(isGift ? "oneTime" : "monthly")} />
				</div>
				<div class="text-3xl/none md:text-4xl/none">
					{#if selectedCurrency.current !== undefined}
						<Formatted
							text={formatPrice(Cart.subscriptionPrice.withTax, selectedCurrency.current, tenant.locale, false)}
						/>
					{/if}
				</div>
			</div>
			{#if savings && cart.subscription.kind === SubscriptionKind.Recurring}
				<div class="text-2xs/none md:text-xs/tighter text-fcrgray mt-1 font-normal">
					<Formatted text={translate("subscriptionSavings", { savings: savings.withTax })} />
				</div>
			{/if}
		</div>
		<ul class="gap-x-1.5 gap-y-1 md:flex-wrap hidden md:flex">
			{#if savings}
				<SubscriptionDiscountChip icon={leaf} label={translate("discount", { discount: savings.percentage })} />
			{/if}
			<SubscriptionDiscountChip icon={box} label={translate("freeShipping")} />
			<SubscriptionDiscountChip icon={shield} label={translate("membership")} />
		</ul>
		<Button
			size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
			variant={ButtonVariant.Dark}
			disabled={Auth.customerType !== CustomerType.B2c}
			onclick={(): void => {
				if (!lastStep) {
					cart.subscriptionModalStep += 1;
				} else if (giftFields) {
					giftFields.requestSubmit();
				} else {
					submit();
				}
			}}
			class="w-full"
		>
			{#if cart.subscriptionModalStep + 1 < maxStep}
				<Formatted text={translate("continue")} />
			{:else if isGift}
				<Formatted text={translate("sendGift")} />
			{:else if cart.hasSubscription}
				<Formatted text={translate("continueToCheckout")} />
			{:else}
				<Formatted text={translate("order")} />
			{/if}
		</Button>
	</div>
</SideModal>
